import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CountryFilter } from './ui/CountryFilter'
import { Button } from './ui/Button'
import { api, extractApiError } from '../lib/api'
import { ALL_COUNTRIES } from '../lib/countries'
import { useAuthStore } from '../stores/authStore'

/**
 * Company Country editor — mirrors CompanyTimezoneEditor. Reads the current
 * value from the session (`authStore.tenant.country`), updates ONLY
 * `Tenant.country` through the shared `PATCH /admin/company` endpoint, then
 * refreshes the session. It never touches billing currency, tax, timezone,
 * or the personal contact number — those are independent settings.
 *
 * Uses the same `CountryFilter` picker the Super-Admin "Create Company" dialog
 * uses, so the UI stays consistent.
 */
export function CompanyCountryEditor({ onSaved, onCountryChange, suggestedCountry }) {
  const tenantCountry = useAuthStore((s) => s.tenant?.country) || ''
  const [draft, setDraft] = useState(tenantCountry)

  // Re-sync when the store's country changes (e.g. right after a successful
  // save calls refreshUser()) — same pattern as the timezone editor.
  useEffect(() => {
    setDraft(tenantCountry)
  }, [tenantCountry])

  // The Company timezone card suggested a matching country → pre-fill the draft
  // so the admin can review and click Save. NOT saved automatically.
  useEffect(() => {
    if (suggestedCountry) setDraft(suggestedCountry)
  }, [suggestedCountry])

  const currentName = useMemo(
    () => ALL_COUNTRIES.find((c) => c.code === tenantCountry)?.name ?? tenantCountry,
    [tenantCountry],
  )
  const unchanged = !draft || draft === tenantCountry

  const mutation = useMutation({
    mutationFn: async (country) => {
      const res = await api.patch('/admin/company', { country })
      return res.data
    },
    onSuccess: async () => {
      toast.success('Company country saved successfully.')
      await useAuthStore.getState().refreshUser()
      onSaved?.()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="min-w-[16rem]">
        <CountryFilter
          id="company-country"
          fullWidth
          placeholder="Select a Country"
          clearLabel={null}
          value={draft}
          onChange={(c) => {
            setDraft(c)
            // Let the parent suggest a matching timezone in the neighbouring
            // card (the admin still Saves it there — nothing auto-saves here).
            onCountryChange?.(c)
          }}
        />
      </div>
      <Button
        onClick={() => mutation.mutate(draft)}
        loading={mutation.isPending}
        disabled={mutation.isPending || unchanged}
      >
        Save Country
      </Button>
      {tenantCountry ? (
        <span className="text-xs text-[color:var(--color-ink-muted)]">
          Current:{' '}
          <span className="font-medium text-[color:var(--color-ink-strong)]">
            {currentName}
          </span>
        </span>
      ) : (
        <span className="text-xs text-[color:var(--color-warning)]">Not set</span>
      )}
    </div>
  )
}
